import React, { useState } from 'react'
import { toast } from '../toast/CustomToast'

const TraceTimeline = ({ steps }) => {
    const [expandedSteps, setExpandedSteps] = useState(new Set())

    const toggleStep = (stepId) => {
        const newExpanded = new Set(expandedSteps)
        if (newExpanded.has(stepId)) {
            newExpanded.delete(stepId)
        } else {
            newExpanded.add(stepId)
        }
        setExpandedSteps(newExpanded)
    }

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text)
            .then(() => toast.success('Conteúdo copiado!'))
            .catch(() => toast.error('Erro ao copiar'))
    }

    const getStatusConfig = (status) => {
        switch (status) {
            case 'completed':
                return {
                    icon: 'check_circle',
                    color: 'text-green-600 dark:text-green-400',
                    bg: 'bg-green-100 dark:bg-green-900/30',
                    border: 'border-green-300 dark:border-green-700',
                    line: 'bg-green-300 dark:bg-green-700',
                    badge: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                }
            case 'failed':
                return {
                    icon: 'error',
                    color: 'text-red-600 dark:text-red-400',
                    bg: 'bg-red-100 dark:bg-red-900/30',
                    border: 'border-red-300 dark:border-red-700',
                    line: 'bg-red-300 dark:bg-red-700',
                    badge: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
                }
            case 'running':
                return {
                    icon: 'autorenew',
                    color: 'text-blue-600 dark:text-blue-400',
                    bg: 'bg-blue-100 dark:bg-blue-900/30',
                    border: 'border-blue-300 dark:border-blue-700',
                    line: 'bg-blue-300 dark:bg-blue-700',
                    badge: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300',
                    animate: true
                }
            case 'skipped':
                return {
                    icon: 'skip_next',
                    color: 'text-gray-500 dark:text-gray-400',
                    bg: 'bg-gray-100 dark:bg-gray-800',
                    border: 'border-gray-300 dark:border-gray-600',
                    line: 'bg-gray-300 dark:bg-gray-600',
                    badge: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                }
            default: // pending
                return {
                    icon: 'radio_button_unchecked',
                    color: 'text-gray-400 dark:text-gray-500',
                    bg: 'bg-gray-50 dark:bg-gray-800',
                    border: 'border-gray-200 dark:border-gray-700',
                    line: 'bg-gray-200 dark:bg-gray-700',
                    badge: 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                }
        }
    }

    const formatDuration = (ms) => {
        if (!ms) return '-'
        if (ms < 1000) return `${ms}ms`
        return `${(ms / 1000).toFixed(2)}s`
    }

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '-'
        const date = new Date(timestamp)
        return date.toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        })
    }

    return (
        <div>
            {/* Timeline */}
            <div className="relative">
                {steps.map((step, index) => {
                    const config = getStatusConfig(step.status)
                    const isExpanded = expandedSteps.has(step.id)
                    const isLast = index === steps.length - 1
                    const hasMetadata = step.step_metadata && Object.keys(step.step_metadata).length > 0
                    const hasError = step.error_message

                    return (
                        <div key={step.id} className="relative">
                            {/* Connecting Line */}
                            {!isLast && (
                                <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700"></div>
                            )}

                            {/* Step Card */}
                            <div className="relative flex gap-4 mb-4">
                                {/* Icon Circle */}
                                <div className={`flex-shrink-0 w-12 h-12 rounded-full ${config.bg} border-2 ${config.border} flex items-center justify-center shadow-sm z-10 transition-all duration-300 ${isExpanded ? 'scale-110' : ''}`}>
                                    <span className={`material-icons ${config.color} ${config.animate ? 'animate-spin' : ''}`} style={{ fontSize: '24px' }}>
                                        {config.icon}
                                    </span>
                                </div>

                                {/* Step Content */}
                                <div className="flex-1 min-w-0">
                                    <div
                                        className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 transition-all duration-300 ${(hasMetadata || hasError) ? 'cursor-pointer hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600' : ''}`}
                                        onClick={() => (hasMetadata || hasError) && toggleStep(step.id)}
                                    >
                                        {/* Step Header */}
                                        <div className="flex items-start justify-between gap-3 mb-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h4 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                                                        {step.step_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                    </h4>
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.badge}`}>
                                                        {step.status}
                                                    </span>
                                                </div>

                                                {/* Timestamps */}
                                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                                                    <span className="flex items-center gap-1">
                                                        <span className="material-icons" style={{ fontSize: '14px' }}>schedule</span>
                                                        {formatTimestamp(step.started_at)}
                                                    </span>
                                                    {step.completed_at && (
                                                        <>
                                                            <span className="material-icons text-gray-400 dark:text-gray-500" style={{ fontSize: '14px' }}>arrow_forward</span>
                                                            <span>{formatTimestamp(step.completed_at)}</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Duration Badge */}
                                            {step.duration_ms !== null && step.duration_ms !== undefined && (
                                                <div className="flex-shrink-0 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                                                        {formatDuration(step.duration_ms)}
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Error Type Badge */}
                                        {step.error_type && (
                                            <div className="mb-2">
                                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 rounded text-xs font-mono">
                                                    <span className="material-icons" style={{ fontSize: '14px' }}>bug_report</span>
                                                    {step.error_type}
                                                </span>
                                            </div>
                                        )}

                                        {/* Expand Indicator */}
                                        {(hasMetadata || hasError) && (
                                            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-2">
                                                <span className="material-icons" style={{ fontSize: '16px' }}>
                                                    {isExpanded ? 'expand_less' : 'expand_more'}
                                                </span>
                                                <span>{isExpanded ? 'Ocultar detalhes' : 'Ver detalhes'}</span>
                                            </div>
                                        )}

                                        {/* Expanded Content */}
                                        {isExpanded && (
                                            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
                                                {/* Error Message */}
                                                {hasError && (
                                                    <div>
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <h5 className="text-sm font-semibold text-red-600 dark:text-red-400 flex items-center gap-2">
                                                                <span className="material-icons" style={{ fontSize: '16px' }}>error_outline</span>
                                                                Erro
                                                            </h5>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    copyToClipboard(step.error_message)
                                                                }}
                                                                title="Copiar erro"
                                                                className="ml-auto text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition"
                                                            >
                                                                <span className="material-icons" style={{ fontSize: '16px' }}>content_copy</span>
                                                            </button>
                                                        </div>
                                                        <pre
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="bg-red-50 dark:bg-red-950/20 text-red-900 dark:text-red-100 p-3 rounded text-xs overflow-x-auto border border-red-200 dark:border-red-800 font-mono whitespace-pre-wrap cursor-text select-all"
                                                        >
                                                            {String(step.error_message).replaceAll('NoneType: None', '')}
                                                        </pre>
                                                    </div>
                                                )}

                                                {/* Metadata */}
                                                {hasMetadata && (
                                                    <div>
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                                                                <span className="material-icons" style={{ fontSize: '16px' }}>info</span>
                                                                Metadados
                                                            </h5>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    copyToClipboard(JSON.stringify(step.step_metadata, null, 2))
                                                                }}
                                                                title="Copiar metadados"
                                                                className="ml-auto text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition"
                                                            >
                                                                <span className="material-icons" style={{ fontSize: '16px' }}>content_copy</span>
                                                            </button>
                                                        </div>
                                                        <div
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="bg-gray-50 dark:bg-gray-900 rounded p-3 space-y-1.5 select-text"
                                                        >
                                                            {Object.entries(step.step_metadata)
                                                                .filter(([key, value]) => {
                                                                    if (key === null || key === undefined || String(key).trim() === '') return false
                                                                    if (value === null || value === undefined) return false
                                                                    if (typeof value === 'string' && value.trim() === '') return false
                                                                    if (Array.isArray(value) && value.length === 0) return false
                                                                    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return false
                                                                    return true
                                                                })
                                                                .map(([key, value]) => (
                                                                <div key={key} className="flex items-start gap-2 text-sm">
                                                                    <span className="font-medium text-gray-600 dark:text-gray-400 min-w-[120px]">
                                                                        {key}:
                                                                    </span>
                                                                    <span className="text-gray-900 dark:text-gray-100 font-mono text-xs break-all">
                                                                        {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>

            {/* Empty State */}
            {steps.length === 0 && (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <span className="material-icons mb-2" style={{ fontSize: '48px' }}>timeline</span>
                    <p>Nenhum step encontrado</p>
                </div>
            )}
        </div>
    )
}

export default TraceTimeline
